import React, { createContext, useState, useEffect, useContext } from 'react';
import { User } from 'firebase/auth';
import { auth, getUserProfile, handleGoogleRedirectResult, updateUserProfile } from '../services/firebase';

// Kendi UserProfile arayüzümüzü tanımlayalım
export interface UserProfile {
  uid: string;
  email: string | null;
  displayName: string | null;
  photoURL: string | null;
  username?: string; // yeni alan
  isAdmin?: boolean;
  isPremium?: boolean;
  phoneNumber?: string;
  role: 'admin' | 'user' | 'moderator'; // user.ts'den alınan özellik
  createdAt: string; // user.ts'den alınan özellik
  updatedAt?: string; // diğer gerekli özellikler
  provider?: 'email' | 'google' | 'facebook' | 'apple';
  premiumSince?: string; // Premium üye olma tarihi
  suspended?: boolean; // Kullanıcı hesap durumu
  suspensionReason?: string | null; // Askıya alma nedeni
  suspendedAt?: string | null; // Askıya alınma tarihi
  privacy?: 'public' | 'members' | 'private';
  avatarVisibility?: 'everyone' | 'members' | 'private';
}

interface AuthContextType {
  currentUser: User | null;
  userProfile: UserProfile | null;
  loading: boolean;
  isAdmin: boolean;
  isPremium: boolean;
  isLoggedIn: boolean;
  refreshUserProfile: () => Promise<void>;
  checkRedirectResult: () => Promise<User | null>;
  updateUserProfileData?: (uid: string, data: Partial<UserProfile>) => Promise<void>;
}

const AuthContext = createContext<AuthContextType>({
  currentUser: null,
  userProfile: null,
  loading: true,
  isAdmin: false,
  isPremium: false,
  isLoggedIn: false,
  refreshUserProfile: async () => {},
  checkRedirectResult: async () => null,
  updateUserProfileData: async () => {}
});

export const useAuth = () => useContext(AuthContext);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);

  // Kullanıcı profilini Firebase'den al
  const fetchUserProfile = async (user: User) => {
    try {
      const profileData = await getUserProfile(user.uid);
      
      // profileData null olmadığından emin olalım
      if (profileData) {
        // Veriyi bizim UserProfile tipine uygun hale getirelim
        const profile: UserProfile = {
          uid: profileData.uid,
          email: profileData.email || null,
          displayName: profileData.displayName || null,
          photoURL: profileData.photoURL || null,
          username: (profileData as any).username || undefined,
          isAdmin: profileData.isAdmin,
          isPremium: profileData.isPremium,
          phoneNumber: profileData.phoneNumber,
          role: profileData.role || 'user',
          createdAt: profileData.createdAt || new Date().toISOString(),
          updatedAt: profileData.updatedAt,
          provider: profileData.provider
          ,privacy: (profileData as any).privacy || 'public'
          ,avatarVisibility: (profileData as any).avatarVisibility || 'everyone'
        };
        // Google ile giriş yapan kullanıcılar için Firestore'da foto yoksa Firebase auth'dan al
        const isGoogleUser = user.providerData.some(p => p.providerId === 'google.com');
        if (isGoogleUser) {
          if (!profile.photoURL && user.photoURL) {
            profile.photoURL = user.photoURL;
            // Firestore'a kaydet (sessiz - hata olursa yut)
            try { await updateUserProfile(user.uid, { photoURL: user.photoURL, provider: 'google' }); } catch {}
          }
          // Provider alanı eksikse set et
          if (!profile.provider) {
            profile.provider = 'google';
          }
        }
        setUserProfile(profile);
      } else {
        // profileData null ise temel bir profil oluşturalım
        const basicProfile: UserProfile = {
          uid: user.uid,
          email: user.email || null,
          displayName: user.displayName || 'Kullanıcı',
          role: 'user',
          createdAt: new Date().toISOString(),
          isPremium: false,
          photoURL: user.photoURL || null,
          provider: user.providerData.some(p=>p.providerId==='google.com') ? 'google' : 'email',
          privacy: 'members',
          avatarVisibility: 'everyone'
        };
        // Eğer google ise ve photoURL varsa Firestore'a yaz
        try {
          if (basicProfile.provider === 'google' && basicProfile.photoURL) {
            await updateUserProfile(user.uid, { photoURL: basicProfile.photoURL, provider: 'google' });
          }
        } catch {}
        setUserProfile(basicProfile);
      }
    } catch (error) {
      console.error("Kullanıcı profili alınırken hata:", error);
      
      // Hata oluşsa bile UI'ın çalışması için temel bir profil oluşturalım
      const basicProfile: UserProfile = {
        uid: user.uid,
        email: user.email || null,
        displayName: user.displayName || 'Kullanıcı',
        role: 'user',
        createdAt: new Date().toISOString(),
        isPremium: false,
  photoURL: user.photoURL || null,
  provider: user.providerData.some(p=>p.providerId==='google.com') ? 'google' : undefined,
  privacy: 'members',
  avatarVisibility: 'everyone'
      };
      
      setUserProfile(basicProfile);
    } finally {
      // Her durumda loading'i false yapalım
      setLoading(false);
    }
  };

  // Kullanıcı profilini yenile
  const refreshUserProfile = async () => {
    if (currentUser) {
      await fetchUserProfile(currentUser);
    }
  };

  // Kullanıcı profilini güncelleme fonksiyonu (PremiumPage için)
  const updateUserProfileData = async (uid: string, data: Partial<UserProfile>) => {
    try {
      // firebase servisinden import ettiğimiz updateUserProfile'ı kullanıyoruz
      await updateUserProfile(uid, data);
      await refreshUserProfile();
    } catch (error) {
      console.error("Profil güncellenirken hata:", error);
      throw error;
    }
  };

  // Yönlendirme sonuçlarını kontrol eden fonksiyon
  const checkRedirectResult = async () => {
    try {
      setLoading(true);
      const user = await handleGoogleRedirectResult();
      if (user) {
        await fetchUserProfile(user);
      }
      return user;
    } catch (error) {
      console.error("Yönlendirme sonucu kontrol edilirken hata:", error);
      return null;
    } finally {
      setLoading(false);
    }
  };

  // Firebase auth durumu değiştiğinde çalışacak
  useEffect(() => {
    setLoading(true); // Auth durumu değiştiğinde loading'i true yap
    
    const unsubscribe = auth.onAuthStateChanged(async (user) => {
      setCurrentUser(user);
      
      if (user) {
        try {
          await fetchUserProfile(user);
        } catch (error) {
          console.error("Auth listener içinde profil hatası:", error);
          // Hata durumunda loading'i false yapmayı unutmayalım
          setLoading(false);
        }
      } else {
        setUserProfile(null);
        setLoading(false);
      }
    });

    return unsubscribe;
  }, []);

  // Bileşen yüklendiğinde yönlendirme sonucunu bir kez kontrol et
  useEffect(() => {
    const checkInitialRedirect = async () => {
      try {
        await checkRedirectResult();
      } catch (error) {
        console.error("İlk yönlendirme kontrolü sırasında hata:", error);
      }
    };
    
    checkInitialRedirect();
  }, []);

  // Hesaplanan özellikler
  const isAdmin = userProfile?.role === 'admin';
  const isPremium = userProfile?.isPremium ?? false;
  const isLoggedIn = !!currentUser;

  const value = {
    currentUser,
    userProfile,
    loading,
    isAdmin,
    isPremium,
    isLoggedIn,
    refreshUserProfile,
    checkRedirectResult,
    updateUserProfileData
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};
